import { useMemo } from 'react';
import { useAtomValue } from 'jotai';

import { ActivityTab } from '@/components/performance/tabs/activity-tab';
import { useActivityFeedQuery } from '@/lib/query-options';
import { useScopedCampaigns } from '@/hooks/usePerformanceData';
import { dateWindow } from '@/lib/performance/date-window';
import {
  organizationDataAtom,
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// The Activity tab is a single full-width timeline (DEFAULT_LAYOUTS.activity);
// the consolidated surface doesn't expose widget reordering, so the layout is
// fixed and reorder/resize are no-ops.
const ACTIVITY_LAYOUT = [{ id: 'activity-content', width: 100 as const, order: 0 }];

/**
 * Consolidated Activity tab (#5971, CP-12). Google change history + Glitch
 * optimization log, reusing the Phase 0 ActivityTab. Google-only for v1 — the
 * tab framework disables it under Meta scope, so this only mounts when Google is
 * in scope.
 */
export function ActivityPanel() {
  const organization = useAtomValue(organizationDataAtom);

  // #6281 — driven by the toolbar instead of a hard-coded 30 days, clamped to
  // the 30-day change_event retention ceiling (the empty state explains it);
  // the campaign filters narrow the Google half of the feed too.
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const dateRange = useMemo(() => {
    const w = dateWindow(Math.min(selectedDays > 0 ? selectedDays : 30, 30));
    return { start_date: w.start_date, end_date: w.end_date };
  }, [selectedDays]);

  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });

  const { data: events, isLoading } = useActivityFeedQuery(
    dateRange.start_date,
    dateRange.end_date,
    organization?.id,
    scopedGoogleAdsIds || undefined,
  );

  return (
    <ActivityTab
      events={events}
      isLoading={isLoading}
      layout={ACTIVITY_LAYOUT}
      onReorder={() => {}}
      onResize={() => {}}
    />
  );
}
